import { useEffect } from "react";
import { IconBaseProps } from "react-icons";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";
import useSortableTable, { SortOrder, Sortable } from "../../hook/useSortableTable";

interface SortIconProps extends IconBaseProps {
  order?: SortOrder;
}

export const SortIcon = (props: SortIconProps) => {
  const { order, ...iconProps } = props;

  return (
    <span className="ms-auto">
      {order === 'asc' && <FaSortUp {...iconProps} />}
      {order === 'desc' && <FaSortDown {...iconProps} />}
      {!order && <FaSort {...iconProps} color="lightgrey" />}
    </span>
  );
};

export interface TableHeaderInfo<T = unknown> {
  header?: string | React.ReactNode;
  accessor?: keyof T;
  presorted?: SortOrder; // presorted order
  sortable?: boolean; // defaults to true if sorted, false if accessor is missing
  hidden?: boolean;
  cssClass?: string;
}

interface TableHeaderProps<T> extends Sortable<T> {
  header: TableHeaderInfo<T>;
  sortingEnabled?: boolean;
  onSort?: (sortField: keyof T) => void
}

const TableHeader = <T,>(props: TableHeaderProps<T>) => {
  const { sortingEnabled = false, sortField, sortOrder, onSort } = props;

  const { header: label, accessor, presorted, hidden = false, cssClass } = props.header;
  const sortable = sortingEnabled && !!accessor && (props.header.sortable ?? !!presorted);
  // aria-sort expects "ascending" and "descending"
  const ariaSort = sortField === accessor
    ? sortOrder === 'asc' ? 'ascending' : 'descending'
    : undefined;

  return sortable ? (
    <th scope="col" hidden={hidden} tabIndex={0} className={cssClass ? `${cssClass} pe-force` : "pe-force" }
      onClick={() => onSort?.(accessor)}
      onKeyUp={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.(accessor)}
      aria-sort={ariaSort}>
      <div className="d-flex text-nowrap">
        {label} <SortIcon order={sortField === accessor ? sortOrder : undefined} />
      </div>
    </th>
  ) : <th scope="col" hidden={hidden} className={cssClass}>{label}</th>;
};

interface TableHeadersProps<T> {
  headers: TableHeaderInfo<T>[];
  sortableData?: T[];
  onSort?: (sortedData: T[]) => void;
}

const TableHeaders = <T,>(props: TableHeadersProps<T>) => {
  const { headers, sortableData, onSort } = props;

  const sortableHeaders = headers.filter(header => !!header.sortable);

  const presortedHeader = headers.find(header => !!header.presorted) ??
    (sortableHeaders.length === 1 ? sortableHeaders[0] : undefined);

  const presorted = presortedHeader && {
    sortField: presortedHeader.accessor,
    sortOrder: presortedHeader.presorted
  } as Sortable<T>;

  const { sortedData, sortField, sortOrder, toggleSorting } = useSortableTable(sortableData || [], presorted);

  useEffect(() => onSort?.(sortedData), [sortedData, onSort]);

  return (
    <thead>
      <tr>
        {headers.map((column, index) =>
          <TableHeader key={column.accessor?.toString() ?? index}
            header={column}
            sortingEnabled={sortableData && sortableData.length > 0}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={toggleSorting} />
        )}
      </tr>
    </thead>
  );
};

export default TableHeaders;
